<template>
	<div class="step2">
		<div class="step2__content">
      <p class="step2__header">Please Select a Date and Time</p>
      <div class="step2__calendar">
        <div v-if="loading" class="step2__loader">
          <div class="step2__loader-spinner"></div>
        </div>
        <div class="step2__upper">
          <select class="step2__select" 
                  name="monthYear" 
                  id="monthYear"
                  @change="onMonthSelect">
            <option v-for="option in monthOptions" 
                    :key="option.value" 
                    :value="option.value" 
                    :selected="option.value === currentMonthValue">
              {{ option.label }}
            </option>
          </select>
          <div class="step2__headerControls">
            <button class="step2__action" :disabled="!canGoToPreviousWeek" @click="changeWeek('prev')">
              <IconChevronLeftBlue/>
            </button>
            <button class="step2__today" @click="backToToday()">Today</button>
            <button class="step2__action" @click="changeWeek('next')">
              <IconChevronRightBlue/>
            </button>
          </div>
        </div>
        <div class="step2__body">

          <div class="step2__navigation">
            <div class="step2__days">
              <div v-for="day in calendar.week"
                   class="step2__day"
                   :class="getDayState(day)">
                <p>{{ day.title }}</p>
                <p>{{ helpers().formatterDayShorthand(day.date) }}</p>
              </div>
            </div>
          </div>
          <div class="step2__timeslots">
            <div v-for="day in calendar.week"
                 class="step2__columns">
              <button v-for="timeslot in day.timeslots"
                      class="step2__timeslot"
                      :class="[{'active': selectedTimeslot === timeslot}]"
                      @click="selectTimeslot(timeslot)">
                {{ helpers().formatterTime(timeslot.dateTime) }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="step2__timeslots">
        <button v-for="timeslot in activeDay.timeslots" 
            :class="[{'active': selectedTimeslot === timeslot}]" 
            @click="selectTimeslot(timeslot)">
          {{ helpers().formatterTime(timeslot.dateTime) }}
        </button>
      </div>
      <label v-if="unavailable" class="step2__notice">We are sorry, but we have no available appointments for this period. Call us to schedule an appointment</label>
		</div>
	</div>
</template>

<script>
import {mapState, mapActions, mapMutations} from 'vuex';
import Services from '../../mixins/services';
import moment from 'moment';
import IconIconChevronLeft from "../../icons/icon-chevron-left.vue";
import IconIconChevronRight from "../../icons/icon-chevron-right.vue";

export default {
	mixins: [Services],
	props: ['clinicId', 'dealerId'],
	components: {
    IconChevronLeftBlue: IconIconChevronLeft,
    IconChevronRightBlue: IconIconChevronRight,
  },
	data: function () {
		return {
      today: moment().format('MM/DD/YYYY'),
      date: moment().format('MM/DD/YYYY'),
      selectedTimeslot: null,
      timeslotRequestId: 0,
      unavailable: false,
			calendar: {
        month: null,
        week: [],
      },
			activeDay: {
				date: null,
				timeslots: [],
			},
		}
	},
	watch: {
		date() {
			this.getAppointmentTimeslotsList()
		},
	},
	computed: mapState({
    loading: state => state.OnlineScheduling.loading,
		appointmentType: state => state.OnlineScheduling.appointmentType,
		appointments: state => state.OnlineScheduling.appointments,
		staff: state => state.OnlineScheduling.staff,
    savedTimeslot: state => state.OnlineScheduling.timeslot,
		clientToken: state => state.ClientToken.clientToken,
    monthOptions() {
      const { start, end } = this.allowedDateRange;

      const options = [];
      let cursor = moment(start);

      while (cursor.isSameOrBefore(end, 'month')) {
        options.push({
          value: cursor.format('MM/YYYY'),
          label: cursor.format('MMMM YYYY')
        });
        cursor.add(1, 'month');
      }

      return options;
    },
    allowedDateRange() {
      const start = moment().startOf('month');
      const end = moment().add(2, 'months').endOf('month');
      return { start, end };
    },
    currentMonthValue() {
      return moment(this.date, 'MM/DD/YYYY').format('MM/YYYY');
    },
    canGoToPreviousWeek() {
      const currentWeekStart = moment().startOf('week');
      const displayedWeekStart = moment(this.date, "MM/DD/YYYY").startOf('week');

      return displayedWeekStart.isAfter(currentWeekStart);
    }
	}),
	methods: {
    ...mapMutations('OnlineScheduling', { setStateValue: 'setStateValue' }),
		...mapActions('OnlineScheduling', { getAppointmentTimeslots: 'getAppointmentTimeslots' }),
    onMonthSelect(e) {
      const [month, year] = e.target.value.split('/');
      this.date = moment(`${month}/01/${year}`, 'MM/DD/YYYY').format('MM/DD/YYYY');
    },
    backToToday() {
      this.initial = true;
      this.date = moment().startOf('month').format('MM/DD/YYYY');
    },
    selectTimeslot(timeslot) {
      this.selectedTimeslot = timeslot;
      this.setStateValue({ key: 'timeslot', value: timeslot });
      this.setStateValue({ key: 'step', value: 3 });
		},
		async getAppointmentTimeslotsList() {
      const requestId = ++this.timeslotRequestId;
      const current = moment(this.date, 'MM/DD/YYYY');
      if (
          current.isBefore(this.allowedDateRange.start) ||
          current.isAfter(this.allowedDateRange.end)
      ) {
        this.unavailable = true;
        this.setStateValue({ key:'loading', value: false });
        return;
      }

      this.setStateValue({ key:'loading', value: true });
      this.unavailable = false;

			this.activeDay = {
				date: null,
				timeslots: []
			}
			
			this.calendar = this.OnlineSchedulingHelpers().buildCalendar({ date: this.date })
			await this.getAppointmentTimeslots({
				startDate: moment(this.calendar.week.find(el => el.title === 'SUN').date, 'MM/DD/YYYY').toISOString(),
				endDate: moment(this.calendar.week.find(el => el.title === 'SAT').date, 'MM/DD/YYYY').toISOString(),
				appointmentType: this.appointmentType,
				clinicId: this.clinicId,
        staffId: this.staff?.id || null,
				dealerId: this.dealerId,
				clientToken: this.clientToken,
				cb: async () => {
          if (requestId !== this.timeslotRequestId) return;

          try {
            this.unavailable = false;
            this.selectedTimeslot = null;
            let foundTimes = false
          
            this.calendar.week.forEach((el) => {
              const dayDate = moment(el.date, 'MM/DD/YYYY');
              let _obj = this.appointments.find(apt => moment(apt.date).format('MM/DD/YYYY') === el.date);
              el.month = dayDate.format("MMMM")
              el.timeslots = _obj && moment(this.today).isBefore(dayDate) ? _obj.timeslots : [];
              if (el.timeslots.length > 0 && el.month === this.calendar.month) {
                foundTimes = true;
              }
            })

            if (this.savedTimeslot) {
              const savedTimeslotDateTime = moment(this.savedTimeslot.dateTime).format();
              this.calendar.week.forEach((el) => {
                const matchingTimeslot = el.timeslots.find(timeslot => moment(timeslot.dateTime).format() === savedTimeslotDateTime);

                if (matchingTimeslot) {
                  this.selectedTimeslot = matchingTimeslot;
                }
              });
            }
          
            if (!foundTimes) {
              if (this.initial) {
                this.changeWeek('next', false)
              } else {
                this.unavailable = true;
              }
            }
          } finally {
            if (requestId === this.timeslotRequestId) {
              this.setStateValue({ key:'loading', value: false });
            }
          }
        }
			})
		},
		getDayState(day) {
      const dayMoment = moment(day.date, 'MM/DD/YYYY');
      const today = moment().startOf('day');

      return {
        'in-past': dayMoment.isBefore(today),
        'no-timeslots': day.timeslots.length === 0,
        'out-of-bounds':
            dayMoment.format('MMMM') !== this.calendar.month ||
            dayMoment.format('YYYY') !== this.calendar.year
      };
		},
		changeWeek(dir, manual = false) {
			if (manual) {
				this.initial = false
			}
			if (dir === 'prev') {
				let _date = moment(this.date, "MM/DD/YYYY").endOf('week').subtract(1, 'weeks').format('MM/DD/YYYY')
				if (moment(_date, "MM/DD/YYYY").startOf('week').isBefore(moment().startOf('week'))) return;
				
				let _weekEndMonth = moment(_date, "MM/DD/YYYY").endOf('week').format('MMMM');
				if (_weekEndMonth === this.calendar.month) {
					this.date = _date;
				} else {
					this.date = moment(this.calendar.month, "MMMM").startOf('month').subtract(1, 'day').format('MM/DD/YYYY');
				}
			} else {
				let _date = moment(this.date, "MM/DD/YYYY").startOf('week').add(1, 'weeks').format('MM/DD/YYYY')
				let _weekStartMonth = moment(_date, "MM/DD/YYYY").startOf('week').format('MMMM');
				if (_weekStartMonth === this.calendar.month) {
					this.date = _date;
				} else {
					this.date = moment(this.calendar.month, "MMMM").startOf('month').add(1, 'month').format('MM/DD/YYYY');
				}
			}
		},
	},
	mounted() {
    if (this.savedTimeslot) {
      const savedDate = moment(this.savedTimeslot.dateTime).format('MM/DD/YYYY');

      this.initial = false;
      this.selectedTimeslot = this.savedTimeslot;

      if (savedDate !== this.date) {
        this.date = savedDate;
        return;
      }
    } else {
      this.initial = true;
    }

    this.getAppointmentTimeslotsList();
  },
  beforeMount() {
    
  }
}
</script>
